import { Popover, PopoverContent, PopoverTrigger } from "@ndd/ui";
import { readRowDetail, readRowLabel, type ReadRowSource } from "./read-row";

/**
 * Composer context line backed by the thread's read ledger, per the comp:
 * italic sand line — "manifest only" before any reads, then
 * "N sources read in this thread — view sources" with the list in a
 * popover; each entry opens the source flyover.
 */
export function SourceLedgerLine({
  sources,
  isNewThread,
  onOpen,
}: {
  sources: ReadRowSource[];
  isNewThread?: boolean;
  onOpen: (source: ReadRowSource) => void;
}) {
  if (sources.length === 0) {
    return (
      <p className="mb-2 text-xs italic text-subtle">
        {isNewThread
          ? "New thread — manifest only, no sources read yet"
          : "Manifest only — no sources read in this thread yet"}
      </p>
    );
  }

  return (
    <p className="mb-2 text-xs italic text-subtle">
      {sources.length} source{sources.length === 1 ? "" : "s"} read in this thread{" "}
      {"— "}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="not-italic font-semibold text-primary hover:text-primary-hover"
          >
            view sources
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" side="top" className="w-80 p-2">
          <span className="block px-2 pb-[6px] pt-[2px] font-sans text-caption font-extrabold uppercase tracking-[0.08em] not-italic text-subtle">
            Sources read in this thread
          </span>
          <span className="flex flex-col">
            {sources.map((source) => (
              <button
                key={`${source.sourceType}:${source.sourceId}`}
                type="button"
                onClick={() => onOpen(source)}
                disabled={source.deleted}
                className="w-full rounded-md px-2 py-[6px] text-left text-xs not-italic text-foreground hover:bg-muted disabled:cursor-default disabled:text-subtle"
              >
                <strong className="font-semibold">{readRowLabel(source)}</strong>{" "}
                <span className="text-muted-foreground">{readRowDetail(source)}</span>
              </button>
            ))}
          </span>
        </PopoverContent>
      </Popover>
    </p>
  );
}
